import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';

import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { NoteService } from '../services/note_service';
import { StreamService } from '../services/stream_service';
import { UserService } from '../services/user_service';
import { Note } from '../types/note';
import { Stream } from '../types/stream';
import { User } from '../types/user';
import { ImageEditorComponent } from '../image-editor/image-editor.component';
import { Util } from '../util/util';


@Component({
  selector: 'note',
  templateUrl: './note-view.component.html',
  styleUrls: ['./note-view.component.scss']
})
export class NoteViewComponent implements OnInit, AfterViewInit {
  @Input()
  public note: Note & { mainThumbnail: string; thumbnailMeta: any; channel?: Stream };
  @Input()
  public stream: Stream;
  @Input()
  public view?: string;
  @Input()
  public showStreamName?: boolean;
  @Input()
  public editNoteAllowed?: boolean;
  @Input()
  public removeNoteButton?: {title: string};
  @Input()
  public saveReturnUrl?: string;

  @Output() noteDeletedEmitter: EventEmitter<any> = new EventEmitter();
  @Output() noteRemoveFromListEmitter: EventEmitter<Note> = new EventEmitter();

  @ViewChild(ImageEditorComponent, {static: false}) private imageEditorComponent: ImageEditorComponent;

  public ownnedNote: boolean;
  public noteOwner: User;

  noteThumb = '';
  ratioThumb = true;
  noteTags = [];
  isLoggedIn = false;
  fileAudioIncluded = false;
  loadingNoteRoute = false;
  public canDeleteNote = false;

  public noteThumbnail: {
    sizeLimit: number;
    limit: boolean;
    other: string | null;
    fileToUpload: any;
    localFile: any;
  } = {
    sizeLimit: 9146000,
    limit: false,
    other: '',
    fileToUpload: '',
    localFile: ''
  };

  public currentLoggedUser: User;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected userService: UserService,
    protected streamService: StreamService,
    private util: Util,
    protected noteService: NoteService,
    protected modalService: NgbModal,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) protected platformId: Object) { }

  public ngOnInit(): void {
    this.ownnedNote = false;
    this.isLoggedIn = this.userService.isLoggedIn();
    if (this.isLoggedIn) {
      const snapshotData = this.route.snapshot.data;
      this.ownnedNote = (snapshotData && snapshotData.user && this.note.userId === snapshotData.user.id) ? true : false;
      if (!this.stream) {
        this.streamService.getStreamInfo(this.note.channelId).then((stream: Stream) => {
          this.note.stream = this.stream;
          this.stream = stream;
          this.canDeleteNote = this.canBeDeleted(this.note, this.stream, snapshotData.user);
        });
      } else {
        this.canDeleteNote = this.canBeDeleted(this.note, this.stream, snapshotData.user);
      }

    this.userService.getProfile()
      .then((user: User) => this.currentLoggedUser = user);
    }

    if (this.note['userId']) {
      this.userService.getUserProfile(this.note.userId)
        .then((user: User) => {
          this.noteOwner = user;
        })
        .catch(err => {
          console.log('error retrieving note author profile', err);
        });
    }

    this.setNoteTags();
  }

  public ngAfterViewInit(): void {
    this.checkForMediaFiles();
  }

  deleteNote() {
    const modalOptions: NgbModalOptions = {};
    const refModalConf = this.modalService.open(ConfirmationModalComponent, modalOptions);

    refModalConf.componentInstance.confirmationModalTitle = 'Delete note';
    refModalConf.componentInstance.confirmationModalMsg = 'Are you sure you want to delete this note?';

    refModalConf.result.then(resp => {
      if (resp) {
        this.noteService.deleteNote(this.note.id).subscribe(() => {
          this.noteDeletedEmitter.emit(this.note);
        },
        err => {
          this.showModalMessage(
            'Delete note',
            'There was an error while removing note. Please try again.'
          );
          console.log('err: ', err);
        });
      }
    });
  }

  showModalMessage(errorTitle, errorMsg) {
    const modalOptions: NgbModalOptions = {};
    const refModalConf = this.modalService.open(ConfirmationModalComponent, modalOptions);

    refModalConf.componentInstance.onlyMessage = true;
    refModalConf.componentInstance.confirmationModalTitle = errorTitle;
    refModalConf.componentInstance.confirmationModalMsg = errorMsg;

    refModalConf.result.then(resp => {
      if (resp) {
        console.log(resp);
      }
    })
      .catch(err => {
        console.log(err);
      });
  }

  public removeFromList(note: Note): void {
    this.noteRemoveFromListEmitter.emit(note);
  }

  getUrlifyName() {
    return this.noteService.urlifyName(this.note.name);
  }

  getLeadWords() {
    return this.note.firstLeadWords || this.note.firstPremWords;
  }

  checkForMediaFiles() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.note.premMedia && this.note.premMedia.filter(m => m.mime && m.mime.indexOf('audio') >= 0).length > 0) {
        this.fileAudioIncluded = true;
      } else {
        setTimeout(() => {
          this.noteService.getNoteInfo(this.note.id, true).then((note: Note) => {
            if (note.premMedia && note.premMedia.filter(m => m.mime && m.mime.indexOf('audio') >= 0).length > 0) {
              this.fileAudioIncluded = true;
            }
          }, e => {
            console.log('Error while getting note info.', e);
          });
        }, 3500);
      }
    }
  }

  public showThumbModal(): void {
    this.imageEditorComponent.editImageModal();
  }

  public setNoteThumbnail(e: any): void {
    this.noteThumbnail.limit = false;
    this.noteThumbnail.other = '';

    const newImg = e['dataURL'];
    const imgSize = newImg.replace(/=/g, '').length * 0.75;

    if (imgSize > this.noteThumbnail.sizeLimit) {
      this.noteThumbnail.limit = true;
    } else {
      fetch(newImg)
        .then(res => res.blob())
        .then(blob => {
          const fileName = e.name ? e.name : `${new Date().getTime()}.${blob.type.split('/')[1]}`;
          const f = new File([blob], fileName, {type: blob.type});

          this.util.downscaleImage(f, 1146000).then(downscaledImg => {
            this.noteThumbnail.fileToUpload = downscaledImg;
            this.uploadThumbnail(this.noteThumbnail.fileToUpload, this.note.id);
          }, err => {
            this.noteThumbnail.fileToUpload = f;
            this.uploadThumbnail(this.noteThumbnail.fileToUpload, this.note.id);
          });
        });

      this.noteThumbnail.localFile = newImg;
    }
  }

  private uploadThumbnail(fileToUpload: any, noteId: string | number): void {
    this.noteService.uploadThumbnail(fileToUpload, noteId.toString()).then(result => {
      if (result.code && result.message) {
        this.showModalMessage('Error while uplaoding Thumbnail', result.message);
      } else {
        Object.assign(this.note, {
          thumbnailImage: result.thumbnailImage,
          thumbnailMeta: result.thumbnailMeta,
          thumbnails: result.thumbnails
        });
      }
    }, err => {
      const errorMessage = 'There was an error while uploading your thumbnail, please try again!';
      console.error(errorMessage, err);
    });
  }

  private canBeDeleted(note: Note, stream: Stream | null, user: User): boolean {
    if (!user || !stream || !note) {
      return false;
    }

    if (user.admin) {
      // global admin content moderator
      return true;
    }

    if (note.userId.toString() === user.id.toString()) {
      // note owner
      return true;
    }

    if (stream.userId.toString() === user.id.toString()) {
      // stream owner
      return true;
    }

    if (stream.admins && stream.admins.indexOf(parseInt(user.id, 10)) !== -1) {
      // stream admin
      return true;
    }

    return false;
  }

  private setNoteTags() {
    if (this.note.tags) {
      this.note.tags.map(tag => {
        const newTag = tag.includes('form-') ? tag.replace('form-', 'type-') : tag;
        const splitTag: any[] = newTag.split('-');

        if (splitTag[0] === 'type' || splitTag[0] === 'category') {
          const tagObj = {
            label: (splitTag.length > 2) ? `${splitTag[1].replace('-', '')} ${splitTag[2]}` : splitTag[1].replace('-', ''),
            code: newTag
          };

          this.noteTags.push(tagObj);
        }
      });
    }
  }
}
